import React, { useState, useMemo, useEffect, useRef } from 'react';
import { BACKEND_CONFIG } from '../config/backendConfig';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '../contexts/AuthContext';
import { useInvitation } from '../contexts/InvitationContext';
import { useReferral } from '../contexts/ReferralContext';
import { AlertCircle, Check, Eye, EyeOff, Mail, Lock, User, Gift, ChevronDown, Smartphone, X } from 'react-feather';
import { useResponsive } from '../hooks/useResponsive';
import { TextInput } from '../components/ui';
import { COACH_APP_OPEN, COACH_SETUP_PATH, authPath, forgetCoachSetup, isCoachEntry, rememberCoachSetup } from '../utils/coachEntry';
import AudienceSwitch from '../components/AudienceSwitch';
import { isCoachApiError, lookupCoachInvite } from '../services/coachAPI';
import type { CoachInviteLookup } from '../services/coachAPI.types';

/**
 * A college coach invite link: /signup?coach=1&invite=<token> (KGH-795).
 * 'open' is the fast path: email fixed, password only, no verification email,
 * straight on to coach setup. 'closed' is a link that is no longer good; the
 * page says so and shows the normal coach sign-up.
 */
type CoachInviteState =
  | { status: 'none' }
  | { status: 'loading' }
  | { status: 'open'; invite: CoachInviteLookup }
  | { status: 'closed'; message: string };

const INVITE_UNREADABLE = 'We could not open your invite. Sign up below.';

// A phone or tablet, where the app can take the invite. Same test as EmailVerifiedPage.
const isPhoneUA = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

// The app's link for a coach invite. It carries the same token as ?invite=.
const coachInviteAppLink = (token: string): string =>
  `apsrecruits://coach-invite?invite=${encodeURIComponent(token)}`;

const SignUpPage: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, signup, emailLogin, error: authError, clearError } = useAuth();
  const { invitationToken, invitationInfo } = useInvitation();
  const { pendingCode } = useReferral();
  const { loginWithRedirect } = useAuth0();
  const [searchParams] = useSearchParams();
  const coachEntry = isCoachEntry(searchParams);
  const coachInviteToken = coachEntry ? searchParams.get('invite') : null;
  // Coach mode sends sign-up on to coach setup. Family mode never does.
  useEffect(() => {
    if (coachEntry) rememberCoachSetup();
    else forgetCoachSetup();
  }, [coachEntry]);
  // Pre-fill the email field from the invitation when present. Cuts down
  // on the typo / wrong-email path that used to trip the hard-block.
  // Doesn't help OAuth users (they pick an account, no email input), but
  // for email/password it removes one whole class of friction.
  const [formData, setFormData] = useState({
    email: invitationInfo?.email || '',
    fullName: '',
    password: '',
    confirmPassword: ''
  });
  // If the invitation arrives after initial mount (deep-link parse races
  // with state init), populate then.
  useEffect(() => {
    if (invitationInfo?.email && !formData.email) {
      setFormData((prev) => ({ ...prev, email: invitationInfo.email! }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationInfo?.email]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // Drives the blur-validation state for the password requirements helper —
  // rules render neutral until the field has been touched, then failing rules
  // turn red.
  const [passwordTouched, setPasswordTouched] = useState(false);
  const errorBannerRef = useRef<HTMLDivElement>(null);

  // The error banner sits above the social buttons and the collapsible email
  // form; the Create Account submit is far below it, so scroll it into view
  // whenever a new error appears.
  useEffect(() => {
    if (error || authError) {
      errorBannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error, authError]);
  // Provider-mismatch state — set when backend reports this email is already
  // registered with a different auth method. UI uses this to show an actionable
  // "Sign in with <provider>" button instead of a generic error.
  const [existingProvider, setExistingProvider] = useState<{ provider: string; label: string } | null>(null);

  // The OAuth-callback path (Google/Apple click) routes the EMAIL_EXISTS_DIFFERENT_PROVIDER
  // error through AuthContext as a colon-separated string. Parse it here so we can
  // render the nice banner instead of the raw string. Format:
  //   EMAIL_EXISTS_DIFFERENT_PROVIDER:<provider>:<label>:<email>:<message>
  const oauthWrongProvider = useMemo(() => {
    if (!authError || !authError.startsWith('EMAIL_EXISTS_DIFFERENT_PROVIDER:')) return null;
    const parts = authError.split(':');
    return {
      provider: parts[1] || 'unknown',
      label: parts[2] || 'another method',
      email: parts[3] || '',
      message: parts.length > 4 ? parts.slice(4).join(':') : '',
    };
  }, [authError]);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendMsg, setResendMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Coach invite link: read the invite before drawing the page, so an invited
  // coach never sees the generic sign-up flash first.
  const [coachInvite, setCoachInvite] = useState<CoachInviteState>(
    coachInviteToken ? { status: 'loading' } : { status: 'none' }
  );
  useEffect(() => {
    if (!coachInviteToken) {
      setCoachInvite({ status: 'none' });
      return;
    }
    const controller = new AbortController();
    setCoachInvite({ status: 'loading' });
    lookupCoachInvite(coachInviteToken, controller.signal)
      .then((invite) => {
        if (controller.signal.aborted) return;
        setCoachInvite({ status: 'open', invite });
        setFormData((prev) => ({ ...prev, email: invite.email }));
        setShowEmailForm(true);
      })
      .catch((e) => {
        // An aborted read (a new token, or the page left) says nothing about
        // the link. Some aborts arrive as an HTTP failure, so check the signal too.
        if (controller.signal.aborted || (e as Error)?.name === 'AbortError') return;
        setCoachInvite({
          status: 'closed',
          // INVITE_NOT_FOUND carries the server's own sentence. Anything else
          // (offline, rate limited) is not the link's fault, so it is not
          // called invalid.
          message: isCoachApiError(e) && e.code === 'INVITE_NOT_FOUND' && e.message ? e.message : INVITE_UNREADABLE,
        });
      });
    return () => controller.abort();
  }, [coachInviteToken]);
  const fastInvite = coachInvite.status === 'open' ? coachInvite.invite : null;

  // "Open in the app" on a phone. If the page still has focus 1.5s after the
  // tap, the app did not open, and the page says so.
  const onPhone = useMemo(() => isPhoneUA(), []);
  const [appOpenFailed, setAppOpenFailed] = useState(false);
  const openInviteInApp = () => {
    if (!coachInviteToken) return;
    setAppOpenFailed(false);
    window.location.href = coachInviteAppLink(coachInviteToken);
    setTimeout(() => {
      if (typeof document !== 'undefined' && document.hasFocus && document.hasFocus()) {
        setAppOpenFailed(true);
      }
    }, 1500);
  };

  // Resend the verification email from the post-signup "Check Your Email"
  // screen, mirroring EmailVerifiedPage.handleResendVerification.
  const handleResendVerification = async () => {
    if (!formData.email || resendingEmail) return;
    setResendingEmail(true);
    setResendMsg(null);
    try {
      const response = await fetch(`${BACKEND_CONFIG.baseURL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email }),
      });
      setResendMsg(response.ok
        ? { ok: true, text: 'Verification email sent. Check your inbox.' }
        : { ok: false, text: "We couldn't resend the email. Please try again in a moment." });
    } catch (err) {
      console.error('Failed to resend verification email:', err);
      setResendMsg({ ok: false, text: "We couldn't resend the email. Please try again in a moment." });
    } finally {
      setResendingEmail(false);
    }
  };


  const handleGoogleLogin = () => {
    loginWithRedirect({
      authorizationParams: {
        connection: 'google-oauth2',
        // Invited coach: offer the invited Google account first.
        ...(fastInvite ? { login_hint: fastInvite.email } : {}),
      }
    });
  };

  const handleAppleLogin = () => {
    loginWithRedirect({
      authorizationParams: {
        connection: 'apple'
      }
    });
  };

  // Show loader while checking authentication, or while reading a coach invite
  if (authLoading || coachInvite.status === 'loading') {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0f0b24',
        fontFamily: 'Manrope, sans-serif',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated mesh gradient background */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(249, 85, 13, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 40% 60%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)
          `,
          opacity: 0.6
        }} />
        
        {/* Animated orbs */}
        <div style={{
          position: 'absolute',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(249, 85, 13, 0.2) 0%, transparent 60%)',
          top: '-150px',
          right: '-100px',
          borderRadius: '50%',
          filter: 'blur(60px)',
          animation: 'float 8s ease-in-out infinite'
        }} />
        <div style={{
          position: 'absolute',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 60%)',
          bottom: '-100px',
          left: '-50px',
          borderRadius: '50%',
          filter: 'blur(60px)',
          animation: 'float 10s ease-in-out infinite reverse'
        }} />

        {/* Loading Spinner */}
        <div style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(249, 85, 13, 0.2)',
            borderTop: '4px solid #f9550d',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '16px',
            fontWeight: '500'
          }}>
            Loading...
          </p>
        </div>

        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            @keyframes float {
              0%, 100% { transform: translate(0, 0) scale(1); }
              50% { transform: translate(30px, -30px) scale(1.05); }
            }
          `}
        </style>
      </div>
    );
  }

  // Redirect if already authenticated. /coach routes a coach by where they are
  // (not registered, pending or verified).
  if (isAuthenticated) {
    return <Navigate to={coachEntry ? '/coach' : '/dashboard'} replace />;
  }

  // Live password-requirements checklist. `met` recomputes every render so the
  // helper reflects each keystroke; the red "failing" treatment only kicks in
  // once the field has been blurred (passwordTouched).
  const passwordChecks = [
    { key: 'length', label: 'At least 8 characters', met: formData.password.length >= 8 },
    { key: 'letter', label: 'Contains a letter', met: /[a-zA-Z]/.test(formData.password) },
    { key: 'number', label: 'Contains a number', met: /[0-9]/.test(formData.password) },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setExistingProvider(null);
    clearError();
    // Treat submit as "touched" so any failing password rules highlight red even
    // if the user never blurred the field.
    setPasswordTouched(true);

    // Validation. The invited coach types one thing: a password.
    if (fastInvite) {
      if (!formData.password) {
        setError('Enter a password.');
        return;
      }
    } else {
      if (!formData.email || !formData.fullName || !formData.password || !formData.confirmPassword) {
        setError('All fields are required');
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }

    // The server's rule (routes/auth.js signup). Keep it the same as the
    // checklist under the password field and the app's sign-up screen.
    if (!passwordChecks.every((c) => c.met)) {
      setError('Your password needs at least 8 characters, with a letter and a number.');
      return;
    }

    setIsLoading(true);

    try {
      // Coach invite fast path. The name sent is the email on purpose: the
      // coach gives their real name on the next screen, and coach setup never
      // pre-fills a name that is an email address. The backend signs the coach
      // straight in (sessionToken) when the token is open and the email is the
      // invited one; otherwise this is a normal coach signup with a
      // verification email, handled by the verifyEmail branch below.
      if (fastInvite) {
        const result = await signup(
          fastInvite.email,
          formData.password,
          fastInvite.email,
          { signupIntent: 'coach', coachInviteToken },
        );
        if (result.invited) {
          navigate(COACH_SETUP_PATH, { replace: true });
        } else if (result.verifyEmail && result.emailVerified) {
          // Created verified, but the server could not sign it in. No email is
          // on its way, so sign in here rather than point at an empty inbox.
          try {
            await emailLogin(fastInvite.email, formData.password);
            navigate(COACH_SETUP_PATH, { replace: true });
          } catch {
            clearError();
            navigate(authPath('/login', true), { replace: true });
          }
        } else if (result.verifyEmail) {
          setSignupSuccess(true);
        }
        setIsLoading(false);
        return;
      }

      // Pass invitation token if present — backend skips the verify-email
      // step and auto-accepts the invitation, returning a session token
      // we sign in with directly (AuthContext.signup handles that).
      const result = await signup(
        formData.email,
        formData.password,
        formData.fullName,
        { inviteToken: invitationToken, signupIntent: coachEntry ? 'coach' : undefined },
      );
      if (result.invited) {
        // Invited path: already signed in. ALWAYS route to quickStart —
        // even guardians need to pick which athlete in the family they're
        // tracking (the auto-accept only links them to the family; the
        // guardian_athlete_relationships row is created by quickstart).
        // quickStart's invited-combined screen reads invitationInfo from
        // context to know we came from an invite, so DO NOT clear it
        // here — that wipes context state before quickStart mounts and
        // we lose the invited-combined branch. The App.tsx ProtectedRoute
        // guard already has an `alreadyInThisFamily` check that prevents
        // the post-auth re-routing to /invitation. clearInvitation runs
        // in quickStart's submit handler after the user finishes setup.
        navigate('/onboarding/quickStart', { replace: true });
      } else if (result.verifyEmail) {
        // Organic path: show "check your inbox" screen.
        setSignupSuccess(true);
      }
      setIsLoading(false);
    } catch (err: any) {
      if (err?.code === 'EMAIL_EXISTS_DIFFERENT_PROVIDER' && err?.primaryProvider) {
        setExistingProvider({
          provider: err.primaryProvider,
          label: err.primaryProviderLabel || 'another method'
        });
        setError('');
      } else {
        setError(err.message || 'Failed to create account. Please try again.');
      }
      setIsLoading(false);
    }
  };

  // Triggered from the provider-mismatch banner. Starts Auth0 universal login
  // on the correct connection so the user can sign in with their original method.
  const handleSignInWithExistingProvider = () => {
    if (!existingProvider) return;
    if (existingProvider.provider === 'google-oauth2') {
      loginWithRedirect({ authorizationParams: { connection: 'google-oauth2' } });
    } else if (existingProvider.provider === 'apple') {
      loginWithRedirect({ authorizationParams: { connection: 'apple' } });
    } else {
      // Password / Auth0 DB — send to /login where they can enter password
      navigate(authPath('/login', coachEntry));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Email Verification Success Screen
  if (signupSuccess) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0f0b24',
        fontFamily: 'Manrope, sans-serif',
        position: 'relative',
        overflow: 'hidden',
        padding: '40px 20px'
      }}>
        {/* Background effects */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(249, 85, 13, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 40% 60%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)
          `,
          opacity: 0.6
        }} />
        
        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: isMobile ? '32px 24px' : '48px',
          maxWidth: '480px',
          width: '100%',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #f9550d 0%, #ff6b2c 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 4px 12px rgba(249, 85, 13, 0.3)'
          }}>
            <Mail size={36} color="white" />
          </div>
          
          <h2 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#151133',
            marginBottom: '12px'
          }}>
            Check Your Email
          </h2>
          
          <p style={{
            fontSize: '16px',
            color: '#f9550d',
            fontWeight: '600',
            marginBottom: '12px'
          }}>
            {formData.email}
          </p>
          
          <p style={{
            fontSize: '15px',
            color: '#64748b',
            lineHeight: '1.6',
            marginBottom: '28px'
          }}>
            We've sent a verification link to your email address. Please click the link to verify your account before signing in.
            {coachEntry && " After you verify, choose I'm a College Coach."}
          </p>
          
          <div style={{
            background: '#f8fafc',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '28px'
          }}>
            <p style={{
              fontSize: '14px',
              color: '#64748b',
              margin: 0
            }}>
              <strong>Tip:</strong> Check your spam or junk folder if you don't see the email in your inbox.
            </p>
          </div>
          
          {/* Verification completes by clicking the emailed link (which mints a
              session and routes to onboarding). Manual sign-in before that just
              bounces off the unverified-email block — so the actionable CTA here
              is Resend, and sign-in is a demoted plain link. */}
          <button
            onClick={handleResendVerification}
            disabled={resendingEmail}
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '16px',
              fontWeight: '600',
              background: 'linear-gradient(135deg, #f9550d 0%, #ff6b2c 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: resendingEmail ? 'not-allowed' : 'pointer',
              fontFamily: 'Manrope, sans-serif',
              boxShadow: '0 4px 12px rgba(249, 85, 13, 0.3)',
              opacity: resendingEmail ? 0.7 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            {resendingEmail ? 'Sending…' : 'Resend verification email'}
          </button>

          {resendMsg && (
            <p style={{
              marginTop: '12px',
              marginBottom: 0,
              fontSize: '13px',
              color: resendMsg.ok ? '#059669' : '#dc2626',
              fontFamily: 'Manrope, sans-serif'
            }}>
              {resendMsg.text}
            </p>
          )}

          <button
            onClick={() => navigate(authPath('/login', coachEntry))}
            style={{
              width: '100%',
              marginTop: '16px',
              padding: '8px',
              fontSize: '14px',
              fontWeight: '600',
              background: 'transparent',
              color: '#64748b',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'Manrope, sans-serif',
              textDecoration: 'underline'
            }}
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  // Google and Apple. Above the email form normally; below it on a coach
  // invite link, where the password field is the one thing to fill in.
  const socialButtons = (
    <>
      <button
        type="button"
        onClick={handleGoogleLogin}
        style={{
          width: '100%',
          padding: '16px',
          marginBottom: '12px',
          background: 'white',
          border: '2px solid #e2e8f0',
          borderRadius: '12px',
          fontSize: '15px',
          fontWeight: '600',
          color: '#1f2937',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          transition: 'all 0.2s ease',
          fontFamily: 'Manrope, sans-serif'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#cbd5e1';
          e.currentTarget.style.background = '#f8fafc';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#e2e8f0';
          e.currentTarget.style.background = 'white';
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Continue with Google
      </button>

      <button
        type="button"
        onClick={handleAppleLogin}
        style={{
          width: '100%',
          padding: '16px',
          marginBottom: fastInvite ? 0 : '24px',
          background: 'black',
          border: '2px solid black',
          borderRadius: '12px',
          fontSize: '15px',
          fontWeight: '600',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          transition: 'all 0.2s ease',
          fontFamily: 'Manrope, sans-serif'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#1f2937';
          e.currentTarget.style.borderColor = '#1f2937';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'black';
          e.currentTarget.style.borderColor = 'black';
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
        </svg>
        Continue with Apple
      </button>
    </>
  );

  const orDivider = (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      marginBottom: '24px',
      gap: '16px'
    }}>
      <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
      <span style={{ 
        fontSize: '13px', 
        color: '#94a3b8',
        fontWeight: '500',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        OR
      </span>
      <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: '#0f0b24',
      fontFamily: 'Manrope, sans-serif',
      position: 'relative',
      overflow: 'hidden',
      padding: isMobile ? '12px' : '40px 20px'
    }}>
      {/* Animated mesh gradient background */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: `
          radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(249, 85, 13, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 40% 60%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)
        `,
        opacity: 0.6
      }} />
      
      {/* Animated orbs */}
      <div style={{
        position: 'absolute',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(249, 85, 13, 0.2) 0%, transparent 60%)',
        top: '-150px',
        right: '-100px',
        borderRadius: '50%',
        filter: 'blur(60px)',
        animation: 'float 8s ease-in-out infinite'
      }} />
      <div style={{
        position: 'absolute',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 60%)',
        bottom: '-100px',
        left: '-50px',
        borderRadius: '50%',
        filter: 'blur(60px)',
        animation: 'float 10s ease-in-out infinite reverse'
      }} />
      <div style={{
        position: 'absolute',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(168, 85, 247, 0.12) 0%, transparent 60%)',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        animation: 'float 12s ease-in-out infinite'
      }} />

      <style>
        {`
          @keyframes float {
            0%, 100% {
              transform: translateY(0px) scale(1);
            }
            50% {
              transform: translateY(-30px) scale(1.05);
            }
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @keyframes pulse {
            0%, 100% {
              box-shadow: 0 0 0 0 rgba(249, 85, 13, 0.4);
            }
            50% {
              box-shadow: 0 0 0 20px rgba(249, 85, 13, 0);
            }
          }

          .logo-pulse {
            animation: pulse 2s ease-in-out infinite;
          }
        `}
      </style>

      {/* Centered Logo Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: '40px',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px'
        }}>
          <div className="logo-pulse" style={{
            width: '80px',
            height: '80px',
            borderRadius: '16px',
            overflow: 'hidden',
            background: 'white',
            padding: '8px',
            flexShrink: 0
          }}>
            <img 
              src="/images/logo-180.png" 
              alt="APS Recruits Logo" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          </div>
          <div>
            <h1 style={{
              margin: 0,
              fontSize: '40px',
              fontWeight: '800',
              letterSpacing: '-0.5px',
              color: 'white'
            }}>
              APS Recruits
            </h1>
            <p style={{
              margin: '4px 0 0 0',
              fontSize: '16px',
              color: 'rgba(255, 255, 255, 0.8)',
              fontWeight: '500'
            }}>
              {coachEntry ? 'College Coach Portal' : 'College Recruiting Platform'}
            </p>
          </div>
        </div>
      </div>

      {/* Centered Container */}
      <div style={{
        width: '100%',
        maxWidth: isMobile ? '100%' : '440px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        zIndex: 1,
        margin: '0 auto'
      }}>
        {/* Sign Up Card */}
        <div style={{
          background: 'white',
          borderRadius: isMobile ? '0' : '16px',
          padding: isMobile ? '24px' : '40px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
        }}>

          {/* Family or coach. Hidden while accepting a family invitation, and
              on a coach invite link, which is only ever for a coach. */}
          {!invitationToken && !fastInvite && <AudienceSwitch coach={coachEntry} />}

          {coachEntry && (
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#151133', margin: '0 0 8px', overflowWrap: 'anywhere' }}>
                {fastInvite
                  ? fastInvite.school_name
                    ? `Set up your ${fastInvite.school_name} coach account`
                    : 'Set up your coach account'
                  : 'Create your coach account'}
              </h2>
              <p style={{ fontSize: '16px', color: '#64748b', margin: 0 }}>
                {fastInvite ? 'Choose a password to get started.' : 'Then set up your coach profile.'}
              </p>
            </div>
          )}

          {/* A coach invite opened on a phone. The app takes the same invite. */}
          {COACH_APP_OPEN && fastInvite && onPhone && coachInviteToken && (
            <>
              <button
                type="button"
                onClick={openInviteInApp}
                style={{
                  width: '100%',
                  padding: '16px',
                  marginBottom: appOpenFailed ? '12px' : '24px',
                  background: 'white',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#1f2937',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  fontFamily: 'Manrope, sans-serif'
                }}
              >
                <Smartphone size={20} aria-hidden />
                Open in the app
              </button>
              {appOpenFailed && (
                <p role="status" style={{ margin: '0 0 24px', fontSize: '14px', lineHeight: 1.5, color: '#64748b', textAlign: 'center' }}>
                  Don&apos;t have the app? Choose a password below.
                </p>
              )}
              {orDivider}
            </>
          )}

          {/* A coach invite link that is no longer good. The normal coach
              sign-up follows, so this is a note, not an error. */}
          {coachInvite.status === 'closed' && (
            <div role="status" style={{
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
              padding: '14px 16px',
              marginBottom: '24px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              fontSize: '14px',
              lineHeight: 1.5,
              color: '#334155'
            }}>
              <AlertCircle size={18} aria-hidden style={{ color: '#64748b', flexShrink: 0, marginTop: 1 }} />
              <span>{coachInvite.message}</span>
            </div>
          )}

          {/* Invitation Banner */}
          {!fastInvite && invitationToken && invitationInfo && (
            <div style={{
              background: '#E3F2FD',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              textAlign: 'center',
              border: '1px solid #1976D2'
            }}>
              <p style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1565C0',
                margin: 0,
                fontFamily: 'Manrope, sans-serif'
              }}>
                You've been invited to join the <strong>{invitationInfo.family_name}</strong>
              </p>
              <p style={{
                fontSize: '14px',
                color: '#1976D2',
                margin: '8px 0 0 0',
                fontFamily: 'Manrope, sans-serif'
              }}>
                Sign in or create an account to accept this invitation
              </p>
            </div>
          )}

          {/* Referral Banner - with change option */}
          {pendingCode && !fastInvite ? (
            <div style={{
              background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
              border: '1px solid #fed7aa',
              borderRadius: '12px',
              padding: '12px 16px',
              marginBottom: '16px',
              fontFamily: 'Manrope, sans-serif'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #f9550d 0%, #e64a0c 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Gift size={16} color="#fff" />
                  </div>
                  <span style={{ fontSize: '14px', color: '#9a3412', fontWeight: '500' }}>
                    Referral code{' '}
                    <span style={{
                      fontFamily: 'monospace',
                      fontWeight: '700',
                      color: '#c2410c',
                      background: '#fff',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}>
                      {pendingCode}
                    </span>
                    {' '}will be applied
                  </span>
                </div>
              </div>
            </div>
          ) : null}


          {existingProvider && (
            <div style={{
              padding: '24px',
              marginBottom: '24px',
              background: 'linear-gradient(135deg, #fef3cd 0%, #fff8e7 100%)',
              border: '2px solid #f9550d',
              borderRadius: '16px',
              textAlign: 'center'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#151133',
                margin: '0 0 8px 0'
              }}>
                Account already exists
              </h3>
              <p style={{
                fontSize: '14px',
                color: '#64748b',
                lineHeight: '1.6',
                margin: '0 0 16px 0'
              }}>
                This email is already registered using {existingProvider.label}. Please sign in with {existingProvider.label} instead.
              </p>
              <button
                type="button"
                onClick={handleSignInWithExistingProvider}
                style={{
                  padding: '12px 28px',
                  fontSize: '14px',
                  fontWeight: '600',
                  background: '#f9550d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: 'Manrope, sans-serif'
                }}
              >
                Sign in with {existingProvider.label}
              </button>
            </div>
          )}

          {/* OAuth-callback wrong-provider banner — fires when user clicked
              Continue-with-Google/Apple and the backend rejected the session
              with EMAIL_EXISTS_DIFFERENT_PROVIDER. Without this the colon-formatted
              error string would render raw in the catch-all banner below. */}
          {!existingProvider && oauthWrongProvider && (
            <div style={{
              padding: '24px',
              marginBottom: '24px',
              background: 'linear-gradient(135deg, #fef3cd 0%, #fff8e7 100%)',
              border: '2px solid #f9550d',
              borderRadius: '16px',
              textAlign: 'center'
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#151133', margin: '0 0 8px 0' }}>
                Account already exists
              </h3>
              <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.6', margin: '0 0 16px 0' }}>
                {oauthWrongProvider.message
                  || `This email is already registered using ${oauthWrongProvider.label}. Please sign in with ${oauthWrongProvider.label} instead.`}
              </p>
              <button
                type="button"
                onClick={() => {
                  // Send to /login so they can pick up the existing account flow
                  // (clears Auth0's lingering session cookie via the login button's prompt:'login').
                  navigate(authPath('/login', coachEntry));
                }}
                style={{
                  padding: '12px 28px',
                  fontSize: '14px',
                  fontWeight: '600',
                  background: '#f9550d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: 'Manrope, sans-serif'
                }}
              >
                Sign in with {oauthWrongProvider.label}
              </button>
            </div>
          )}

          {!existingProvider && !oauthWrongProvider && (error || authError) && (
            <div ref={errorBannerRef} style={{
              padding: '16px',
              marginBottom: '24px',
              background: '#fee',
              border: '1px solid #fcc',
              borderRadius: '12px',
              color: '#c00',
              fontSize: '14px'
            }}>
              <strong>Error:</strong> {error || authError}
            </div>
          )}

          {!fastInvite && (
            <>
              {socialButtons}
              {orDivider}
            </>
          )}

          {/* Collapsible Email Form Toggle. A coach invite link opens straight
              onto the form, so there is nothing to toggle. */}
          {!fastInvite && (
          <button
            type="button"
            onClick={() => setShowEmailForm(!showEmailForm)}
            style={{
              width: '100%',
              padding: '16px',
              marginBottom: showEmailForm ? '24px' : '0',
              background: 'white',
              border: '2px solid #e2e8f0',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '600',
              color: '#1f2937',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              fontFamily: 'Manrope, sans-serif'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#cbd5e1';
              e.currentTarget.style.background = '#f8fafc';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e2e8f0';
              e.currentTarget.style.background = 'white';
            }}
          >
            <Mail size={18} />
            Sign up with Email
            <span style={{
              marginLeft: 'auto',
              transition: 'transform 0.2s ease',
              transform: showEmailForm ? 'rotate(180deg)' : 'rotate(0deg)',
              display: 'inline-flex',
              alignItems: 'center'
            }}>
              <ChevronDown size={18} aria-hidden="true" />
            </span>
          </button>
          )}

          {/* Email Form - Collapsible */}
          {(showEmailForm || fastInvite) && (
            <form onSubmit={handleSubmit}>
            {/* Email Input */}
            <div style={{ marginBottom: '20px' }}>
              {/* The label now points at the control (it never did), and the
                  icon rides the primitive's leftIcon slot rather than a
                  hand-positioned absolute span. The onFocus/onBlur pair that
                  painted the orange border by hand is gone: .aps-input:focus
                  does it, and dropping the inline `outline: none` is what
                  restores the keyboard focus ring on the whole sign-up form. */}
              <label htmlFor="signup-email" style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#151133',
                marginBottom: '8px'
              }}>
                Your Email
              </label>
              {/* On a coach invite link the address is the invited one and
                  cannot change: the invite link is what proves the mailbox. */}
              <TextInput
                id="signup-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="email@example.com"
                required
                readOnly={!!fastInvite}
                aria-readonly={fastInvite ? true : undefined}
                autoComplete={fastInvite ? 'username' : undefined}
                style={fastInvite ? { background: 'var(--aps-fill)', color: 'var(--aps-text-2)', cursor: 'default' } : undefined}
                leftIcon={<Mail size={20} />}
              />
            </div>

            {/* Full Name Input. Asked on coach setup instead on the invite path. */}
            {!fastInvite && (
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="signup-full-name" style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#151133',
                marginBottom: '8px'
              }}>
                Your Name
              </label>
              <TextInput
                id="signup-full-name"
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="John Doe"
                required
                leftIcon={<User size={20} />}
              />
            </div>
            )}

            {/* Password Input */}
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="signup-password" style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#151133',
                marginBottom: '8px'
              }}>
                Password
              </label>
              {/* onBlur keeps the half that mattered: it arms the requirements
                  helper below. Only the border repaint left with it. */}
              {/* One password field on the invite path, with a show button in
                  place of the confirm field. */}
              <div style={{ position: 'relative' }}>
                <TextInput
                  id="signup-password"
                  type={fastInvite && showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password"
                  required
                  autoFocus={!!fastInvite}
                  autoComplete="new-password"
                  aria-describedby="password-requirements"
                  leftIcon={<Lock size={20} />}
                  style={fastInvite ? { paddingRight: '52px' } : undefined}
                  onBlur={() => setPasswordTouched(true)}
                />
                {fastInvite && (
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bottom: 0,
                      width: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '0 8px 8px 0',
                      color: '#64748b',
                      cursor: 'pointer'
                    }}
                  >
                    {showPassword ? <EyeOff size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
                  </button>
                )}
              </div>
              {/* Persistent password-requirements helper (#204). Rules were
                  previously hidden in the placeholder and only checked at
                  submit; now they're always visible, reflect which pass live,
                  and turn red on blur when unmet. */}
              <div id="password-requirements" style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {passwordChecks.map((c) => {
                  const showUnmet = passwordTouched && !c.met;
                  const color = c.met ? '#059669' : showUnmet ? '#dc2626' : '#94a3b8';
                  return (
                    <div
                      key={c.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '12px',
                        fontWeight: 500,
                        color,
                        fontFamily: 'Manrope, sans-serif',
                      }}
                    >
                      {c.met
                        ? <Check size={14} aria-label="Requirement met" />
                        : <X size={14} aria-label="Requirement not met" />}
                      {c.label}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Confirm Password Input */}
            {!fastInvite && (
            <div style={{ marginBottom: '24px' }}>
              <label htmlFor="signup-confirm-password" style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#151133',
                marginBottom: '8px'
              }}>
                Confirm Password
              </label>
              <TextInput
                id="signup-confirm-password"
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Re-enter password"
                required
                leftIcon={<Lock size={20} />}
              />
            </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '16px',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #f9550d 0%, #ff6b2c 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.6 : 1,
                marginBottom: '16px',
                fontFamily: 'Manrope, sans-serif',
                boxShadow: '0 4px 12px rgba(249, 85, 13, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(249, 85, 13, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 85, 13, 0.3)';
              }}
            >
              {isLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <style>
                    {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
                  </style>
                  Creating account...
                </div>
              ) : (
                fastInvite ? 'Continue' : 'Create Account'
              )}
            </button>
          </form>
          )}

          {/* Coach invite link: Google and Apple still work, below the form. */}
          {fastInvite && (
            <>
              {orDivider}
              {socialButtons}
            </>
          )}

          {/* Sign In Link */}
          <div style={{ ...footerLine, marginTop: '24px' }}>
            Already have an account?{' '}
            <a href={authPath('/login', coachEntry)} style={footerLink} {...footerLinkHover}>
              Sign In
            </a>
          </div>


          {/* Terms */}
          <div style={{
            marginTop: '24px',
            fontSize: '12px',
            color: '#94a3b8',
            textAlign: 'center',
            lineHeight: '1.5'
          }}>
            By creating an account, you agree to our{' '}
            <a href="https://apsrecruiting.io/terms" style={{ color: '#f9550d', textDecoration: 'none' }}>Terms of Service</a>
            {' '}and{' '}
            <a href="https://apsrecruiting.io/privacy" style={{ color: '#f9550d', textDecoration: 'none' }}>Privacy Policy</a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Footer lines under the card. Shared so every line and link matches.
const footerLine: React.CSSProperties = {
  textAlign: 'center',
  fontSize: '14px',
  color: '#64748b',
};
const footerLink: React.CSSProperties = {
  color: '#f9550d',
  textDecoration: 'none',
  fontWeight: '600',
  transition: 'color 0.2s ease',
};
const footerLinkHover = {
  onMouseEnter: (e: React.MouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.color = '#ff6b2c'; },
  onMouseLeave: (e: React.MouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.color = '#f9550d'; },
};

export default SignUpPage;
